import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useLmxStore } from './lmxStore';
import { useLmxTurn } from './useLmxTurn';
import { useLmxTether } from './useLmxTether';
import { LmxBubble } from './LmxBubble';
import { LmxPanel } from './LmxPanel';
import { useWorkflowStore } from '../store/workflowStore';
import { useCanvasNavStore } from '../store/canvasNavStore';
import useTimelineStore from '../store/timelineStore';
import { useAuth } from '@clerk/clerk-react';
import './lmx.css';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * LmxLayer — la capa de Lumière (LMX). Vive encima de la plataforma y es
 * la ÚNICA pieza de UI del agente nuevo: telón, burbuja, línea de luz y
 * chat. Nada de esto toca el Lumière legacy.
 */
export const LmxLayer = () => {
  const { getToken } = useAuth();
  const { mode, available, busy, notify, pose, veil, building, assets } = useLmxStore();
  const store = useLmxStore;
  const { send, approve, cancel } = useLmxTurn();
  const projectId = useWorkflowStore((s) => s.projectId);
  const setSearchOpen = useCanvasNavStore((s) => s.setSearchOpen);
  const requestFocus = useCanvasNavStore((s) => s.requestFocus);
  const timelineOpen = useTimelineStore((s) => s.isOpen);

  const layerRef = useRef(null);
  const orbRef = useRef(null);
  const panelRef = useRef(null);
  const tetherRef = useRef(null);

  const open = mode === 'open';
  useLmxTether({ layerRef, orbRef, panelRef, tetherRef, open });

  // Altura de la barra superior del Studio: la capa arranca debajo.
  useEffect(() => {
    const measure = () => {
      const nav = document.querySelector('.top-nav, .studio-topnav, nav.top-nav-tiered');
      const h = nav?.offsetHeight || 56;
      document.documentElement.style.setProperty('--lmx-top', `${h}px`);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // Al entrar al Studio, el telón con la burbuja grande al centro.
  useEffect(() => {
    store.setState({ mode: 'curtain' });
  }, [store]);

  // Rehidratación: la charla sigue donde quedó al recargar.
  useEffect(() => {
    if (!available) return undefined;
    let alive = true;
    (async () => {
      try {
        const token = await getToken();
        const url = `${API}/api/lmx/conversation${projectId ? `?project_id=${projectId}` : ''}`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok || !alive) return;
        store.getState().hydrate(await res.json());
      } catch { /* sin historial, se abre limpia */ }
    })();
    return () => { alive = false; };
  }, [available, projectId, getToken, store]);

  // La burbuja grande y centrada mientras hay telón.
  const layoutOrb = useCallback(() => {
    const layer = layerRef.current; const orb = orbRef.current;
    if (!layer || !orb) return;
    if (mode !== 'curtain') {
      orb.style.setProperty('--lmx-ox', '0px');
      orb.style.setProperty('--lmx-oy', '0px');
      orb.style.setProperty('--lmx-os', 1);
      return;
    }
    const w = orb.offsetWidth;
    const bx = orb.offsetLeft + w / 2; const by = orb.offsetTop + w / 2;
    const s = Math.min(2.6, (layer.clientWidth * 0.6) / w);
    const size = w * s;
    const cx = layer.clientWidth / 2;
    const cy = Math.max(size / 2 + 24, Math.min(layer.clientHeight * 0.42, 320));
    orb.style.setProperty('--lmx-ox', `${cx - bx}px`);
    orb.style.setProperty('--lmx-oy', `${cy - by}px`);
    orb.style.setProperty('--lmx-os', s);
    const hint = layer.querySelector('.lmx-orb-hint');
    if (hint) hint.style.top = `${cy + size / 2 + 18}px`;
  }, [mode]);

  useLayoutEffect(() => {
    layoutOrb();
    const layer = layerRef.current;
    if (!layer || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(layoutOrb);
    ro.observe(layer);
    return () => ro.disconnect();
  }, [layoutOrb, available]);

  // Alto del panel: la burbuja se apoya sobre su borde superior.
  useLayoutEffect(() => {
    const layer = layerRef.current; const panel = panelRef.current;
    if (!layer || !panel || !open) return undefined;
    const apply = () => layer.style.setProperty('--lmx-ph', `${panel.offsetHeight}px`);
    apply();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(apply);
    ro.observe(panel);
    return () => ro.disconnect();
  }, [open]);

  // Esc minimiza (si no estás escribiendo).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (store.getState().mode !== 'open') return;
      if (document.activeElement?.dataset?.testid === 'lmx-input') return;
      store.getState().collapse();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [store]);

  useEffect(() => {
    const onOpen = () => store.getState().open();
    window.addEventListener('lmx:open', onOpen);
    return () => window.removeEventListener('lmx:open', onOpen);
  }, [store]);

  const submit = useCallback((text) => {
    const value = (text || '').trim();
    if (!value || store.getState().busy) return;
    store.getState().markGreeted();
    send({ message: value, projectId, screen: 'canvas' });
  }, [projectId, send, store]);

  const onAction = useCallback((action) => {
    if (!action) return;
    if (action.type === 'message') { submit(action.text); return; }
    if (action.type === 'focus_node') {
      requestFocus(action.node_id);
      store.getState().collapse();
      return;
    }
    if (action.type === 'approve') { approve(action.confirm_id, projectId); return; }
    if (action.type === 'cancel') { cancel(action.confirm_id, projectId); }
  }, [approve, cancel, projectId, requestFocus, store, submit]);

  const goNode = useCallback(() => {
    store.getState().collapse();
    setSearchOpen(true);
  }, [setSearchOpen, store]);

  // El editor de timeline usa el mismo borde inferior: LMX se aparta.
  useEffect(() => {
    if (timelineOpen && store.getState().mode !== 'docked') {
      store.getState().collapse();
    }
  }, [timelineOpen, store]);

  if (!available) return null;
  if (timelineOpen) return null;

  // Telón: opaco al entrar, esmerilado con el chat, claro mientras construye
  // o con el chat cerrado.
  const telon = mode === 'curtain' ? ''
    : (!open || building || !veil) ? 'is-clear' : 'is-frosted';

  return (
    <>
      <div
        className={`lmx-telon ${telon}`}
        style={{ '--lmx-telon-img': `url("${assets.curtain_bg}")` }}
        aria-hidden
        data-testid="lmx-telon"
        onClick={() => { if (mode === 'curtain') store.getState().open(); }}
      />

      <div
        className={`lmx-layer ${open ? 'is-open' : mode === 'docked' ? 'is-docked' : 'is-collapsed'}`}
        ref={layerRef}
        aria-label="Lumière, agente de Studio Media Labs"
        data-testid="lmx-layer"
      >
        <svg className="lmx-tether" ref={tetherRef} aria-hidden>
          <defs>
            <filter id="lmxTetherBlur" x="-20%" y="-50%" width="140%" height="200%">
              <feGaussianBlur stdDeviation="2.4" />
            </filter>
            <filter id="lmxSparkBlur" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="5" />
            </filter>
          </defs>
          <path className="lmx-t-rglow" /><path className="lmx-t-rcore" />
          <path className="lmx-t-glow" /><path className="lmx-t-core2" /><path className="lmx-t-core" />
          <path className="lmx-t-spark lmx-halo lmx-t-sah" />
          <path className="lmx-t-spark lmx-halo lmx-t-sbh" />
          <path className="lmx-t-spark lmx-t-sa" /><path className="lmx-t-spark lmx-t-sb" />
        </svg>

        <button
          type="button"
          className={`lmx-orb ${busy && !open ? 'is-busy' : ''} ${notify && !open ? 'is-notify' : ''}`}
          ref={orbRef}
          onClick={() => store.getState().toggle()}
          aria-expanded={open}
          aria-label={open ? 'Minimizar a Lumière' : 'Abrir a Lumière'}
          data-testid="lmx-orb"
        >
          <span className="lmx-dot" />
          <img src={assets.orb} alt="" />
        </button>

        {mode === 'curtain' && (
          <p className="lmx-orb-hint" data-testid="lmx-orb-hint">
            Tocá la burbuja para empezar.
          </p>
        )}

        <div className="lmx-stage" data-testid="lmx-stage">
          <div className="lmx-halo" aria-hidden />
          <LmxPanel panelRef={panelRef} onSubmit={submit} onGoNode={goNode} />
          <div className="lmx-avatar" data-pose={pose} data-avatar="orb"
               aria-hidden={false} data-testid="lmx-avatar">
            <div className="lmx-av-orb" aria-hidden>
              <span className="lmx-gl" /><span className="lmx-spin" />
              <span className="lmx-rip" /><span className="lmx-rip r2" />
              <img src={assets.avatar_orb} alt="" />
            </div>
            <LmxBubble onAction={onAction} />
          </div>
        </div>
      </div>
    </>
  );
};

export default LmxLayer;
